import { ViewController } from '../../ViewController';
import { ServiceBusController } from '../ServiceBusController';
import {
    ServiceQueries,
    query,
    serviceQueries,
    ServiceEvents,
    ServiceCommands
} from '@estee/elc-service-bus';
import { BaseService } from '../BaseService';

type diContainerType = { get<T>(key: string): T };
export type ContainerImport = () => Promise<{ diContainer: diContainerType }>;

export type ServiceBusControllerBody = {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    [key: string]: Promise<(...args: any[]) => any> | Function;
};

export class ServiceManagerClass {
    public serviceName: string;
    private containerImport: ContainerImport;
    private diContainer: diContainerType;
    private serviceKey: string;
    private service: BaseService;
    private serviceBusControllerKey: string;
    private serviceBusBaseClass: ServiceBusController & ServiceBusControllerBody;

    get serviceHasStarted(): boolean {
        return Boolean(this.service && this.service.isStarted);
    }

    private async getContainer() {
        if (!this.diContainer) {
            this.diContainer = (await this.containerImport()).diContainer;
        }

        return this.diContainer;
    }

    public setServiceStartParams({
        serviceName,
        diContainerImport,
        serviceKey,
        serviceBusControllerKey,
        serviceBusBaseClass
    }: {
        serviceName: string;
        diContainerImport: ContainerImport;
        serviceKey: string;
        serviceBusControllerKey: string;
        serviceBusBaseClass: ServiceBusController & ServiceBusControllerBody;
    }) {
        this.containerImport = diContainerImport;
        this.serviceName = serviceName;
        this.serviceKey = serviceKey;
        this.serviceBusControllerKey = serviceBusControllerKey;
        this.serviceBusBaseClass = serviceBusBaseClass;
    }

    public getServiceBusControllerMethod(functionName: string) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return async (...args: any[]) => {
            await this.startService();
            const diContainer = await this.getContainer();
            const srvBusController = diContainer.get<ServiceBusController>(
                this.serviceBusControllerKey
            );
            const method = (srvBusController as ServiceBusControllerBody)[functionName] as Function;

            return method.call(null, ...args);
        };
    }
    public setDecoratorOnFunction(decorator: Function) {
        return ([trigger, functionName]: [
            trigger: keyof ServiceQueries | keyof ServiceEvents | keyof ServiceCommands,
            functionName: string
        ]) => {
            decorator(trigger)(this.serviceBusBaseClass.__proto__, functionName);
            this.serviceBusBaseClass[functionName] = this.getServiceBusControllerMethod(
                functionName
            );
        };
    }

    public async startService() {
        await this.getContainer();
        this.service = this.diContainer.get<BaseService>(this.serviceKey);
        const config = await query(serviceQueries.GET_CONFIG, ['ecommConfig']);
        if (!config || !config.ecommConfig) {
            throw new Error('EcommConfig not found');
        }

        await this.service.start(config.ecommConfig);
    }

    public getController(controllerKey?: string): ViewController | undefined {
        if (!this.diContainer) {
            throw new Error("Can't request controller without starting the service first");
        }

        const controller = controllerKey
            ? this.diContainer.get<ViewController>(controllerKey)
            : undefined;

        return controller;
    }
}

export const ServiceManager = new ServiceManagerClass();
